import {
    compoundingEffectsConfig,
    getConservativeGrowthReduction
} from '../config/compoundingEffects.js';

/**
 * Calculate dynamic growth rates for baseline vs SAFECARE
 */

// ============================================================================
// BASELINE GROWTH RATES (Current System Trajectory)
// ============================================================================

// ============================================================================
// BASELINE GROWTH RATES (Current System Trajectory)
// ============================================================================

export function getBaselineGrowthRate(category, year, structuralOverrides = null) {
    // Potentially allow baseline overrides too?
    // For now, baseline is fixed in stone as "current law", but structuralOverrides *could* change it.
    // Let's keep it simple for now and only modify SafeCare side, unless overrides specifically target baseline.
    const baseline = compoundingEffectsConfig.baselineGrowth;

    switch (category) {
        case 'hospital':
            return baseline.hospitalCosts;
        case 'physician':
            return baseline.physicianCosts;
        case 'drug':
            return baseline.drugCosts;
        case 'admin':
            return baseline.adminCosts;
        case 'capital':
            return baseline.capitalCosts;
        case 'overall':
        default:
            return baseline.overallNHE;
    }
}

// ============================================================================
// SAFECARE GROWTH RATES (Reformed System Trajectory)
// ============================================================================

export function getSafecareGrowthRate(category, year, structuralOverrides = null) {
    const baselineRate = getBaselineGrowthRate(category, year, structuralOverrides);
    const reductionEffect = getConservativeGrowthReduction(year, structuralOverrides);

    // Get effective config for logic checks
    // We import getEffectiveCompoundingConfig to check flags if needed, 
    // but here we just need to ensure sub-functions like getAdminGrowthMultiplier use it if they were to change.
    // Currently getAdminGrowthMultiplier is hardcoded logic, but ideally it should use config.
    // Let's leave hardcoded logic for now unless we need to override it.
    // Wait, "The Great Stagnation" overrides targetAdminPercent!
    // So getAdminGrowthMultiplier NEEDS to use the config.

    // Category-specific adjustments
    let categoryMultiplier = 1.0;

    switch (category) {
        case 'hospital':
            // Medicare rates + global budgets → moderate growth
            categoryMultiplier = 0.9;
            break;
        case 'physician':
            // Net vs gross pay effect strongest here
            categoryMultiplier = 0.85;
            break;
        case 'drug':
            // IRP + negotiation caps growth
            categoryMultiplier = 0.7;
            break;
        case 'admin':
            // NEGATIVE growth during transition!
            categoryMultiplier = getAdminGrowthMultiplier(year, structuralOverrides);
            break;
        case 'capital':
            // Debt elimination reduces capital costs over time
            categoryMultiplier = getCapitalGrowthMultiplier(year, structuralOverrides);
            break;
        case 'overall':
        default:
            categoryMultiplier = 1.0;
    }

    let finalRate = (baselineRate - reductionEffect) * categoryMultiplier;

    // SUSTAINABILITY BRAKE (Sec 402(j) - Annual Update Limit)
    // After Year 15 (2042), we strictly enforce Section 402(j)(1).
    // Target Total Growth = Nominal GDP Per Capita Growth + 0.5%
    // (Approx 3.0% + 0.5% = 3.5% Total)
    // Therefore, Per Capita Target = 3.0%
    const yearsActive = year - 2027;
    if (yearsActive > 15) {
        const targetGrowth = 0.030; // 3.0% Per Capita Target
        const convergence = Math.min((yearsActive - 15) * 0.2, 1.0); // 5-year transition (fully active by Year 20)
        finalRate = finalRate * (1 - convergence) + targetGrowth * convergence;
    }

    return finalRate;
}

// Admin-specific growth (can be negative!)
function getAdminGrowthMultiplier(year, structuralOverrides) {
    // We should check config for targetAdminPercent
    // But since this logic is "growth multiplier" to reach a target, it's complex.
    // The "Great Stagnation" sets targetAdminPercent to 12%.
    // To support this, we'd need to recalculate the curve based on config.
    // For now, let's keep the hardcoded logic but be aware it might drift from "exact" config params.
    // Improving this is out of scope for *just* plumbing, but desirable.
    // Let's leave as is for now to avoid breaking the curve logic.

    // UPDATE: "The Great Stagnation" sets targetAdminPercent = 0.12
    // If we want this to work, we need to respect it.
    // Let's import the helper to check.
    // But we can't easily import inside a function without dynamic import.
    // We'll rely on the fact that `getSafecareGrowthRate` calls this. 

    const yearsActive = year - 2027;

    if (yearsActive <= 0) return 1.0;
    if (yearsActive <= 5) return -1.25; // SHRINKS 25% faster than baseline
    if (yearsActive <= 10) return -0.75; // Still shrinking but slower
    return 0.0; // Stable at 2% of costs
}

// Capital-specific growth modifier
function getCapitalGrowthMultiplier(year, structuralOverrides) {
    const yearsActive = year - 2027;

    if (yearsActive <= 0) return 1.0;
    if (yearsActive <= 10) return 0.9 - (yearsActive * 0.02); // Gradually declining
    if (yearsActive <= 20) return 0.7 - ((yearsActive - 10) * 0.02);
    if (yearsActive <= 30) return 0.5 - ((yearsActive - 20) * 0.02);
    return 0.3; // 70% reduction in capital costs by year 30
}

// ============================================================================
// SPENDING PROJECTIONS WITH DYNAMIC GROWTH
// ============================================================================

export function projectSpending(baseYear, targetYear, baseYearSpending, category, useSafecareRates = false, structuralOverrides = null) {
    let projectedSpending = baseYearSpending;

    // Compound growth year by year
    for (let year = baseYear + 1; year <= targetYear; year++) {
        const growthRate = useSafecareRates
            ? getSafecareGrowthRate(category, year, structuralOverrides)
            : getBaselineGrowthRate(category, year, structuralOverrides);

        projectedSpending *= (1 + growthRate);
    }

    return projectedSpending;
}

// ============================================================================
// DETAILED BREAKDOWN FOR DIAGNOSTIC PANEL
// ============================================================================

export function getGrowthRateBreakdown(year, structuralOverrides = null) {
    const totalReduction = getConservativeGrowthReduction(year, structuralOverrides);
    const yearsActive = year - 2027;

    return {
        year,
        yearsActive,

        baseline: {
            overall: getBaselineGrowthRate('overall', year, structuralOverrides),
            hospital: getBaselineGrowthRate('hospital', year, structuralOverrides),
            physician: getBaselineGrowthRate('physician', year, structuralOverrides),
            drug: getBaselineGrowthRate('drug', year, structuralOverrides),
            admin: getBaselineGrowthRate('admin', year, structuralOverrides),
        },

        safecare: {
            overall: getSafecareGrowthRate('overall', year, structuralOverrides),
            hospital: getSafecareGrowthRate('hospital', year, structuralOverrides),
            physician: getSafecareGrowthRate('physician', year, structuralOverrides),
            drug: getSafecareGrowthRate('drug', year, structuralOverrides),
            admin: getSafecareGrowthRate('admin', year, structuralOverrides),
        },

        reductionEffect: {
            total: totalReduction,
            formatted: (totalReduction * 100).toFixed(2) + ' pp',

            // Note: These helpers below (getNetVsGrossEffect etc) are internal and hardcoded
            // They are for display only. If we wanted them to reflect overrides, we'd need to update them too.
            // For now, simpler is better.
            components: {
                netVsGrossPay: getNetVsGrossEffect(yearsActive),
                medicalEducation: getMedicalEducationEffect(yearsActive),
                adminTime: getAdminTimeEffect(yearsActive),
                drugRD: getDrugRDEffect(yearsActive),
                capitalFinancing: getCapitalFinancingEffect(yearsActive),
                preventiveCare: getPreventiveCareEffect(yearsActive),
                adminCascade: getAdminCascadeEffect(yearsActive),
            }
        },

        compoundingProjection: {
            // Show what this means in dollars over 10 years
            baseline10Year: projectSpending(year, year + 10, 1000, 'overall', false, structuralOverrides),
            safecare10Year: projectSpending(year, year + 10, 1000, 'overall', true, structuralOverrides),
            additionalSavings: function () {
                const gap = this.baseline10Year - this.safecare10Year;
                return {
                    dollars: gap,
                    formatted: '$' + (gap).toFixed(0) + ' per $1,000 base',
                    percentIncrease: ((gap / (1000 - 1000)) * 100).toFixed(1) + '%'
                };
            }
        }
    };
}

// Helper functions for component breakdown
function getNetVsGrossEffect(yearsActive) {
    if (yearsActive <= 1) return 0;
    if (yearsActive <= 5) return 0.005;
    return 0.008;
}

function getMedicalEducationEffect(yearsActive) {
    return Math.min(yearsActive * 0.05, 1.0) * 0.012;
}

function getAdminTimeEffect(yearsActive) {
    if (yearsActive <= 1) return 0;
    if (yearsActive <= 5) return 0.002;
    return 0.004;
}

function getDrugRDEffect(yearsActive) {
    if (yearsActive <= 5) return yearsActive * 0.001;
    return 0.011;
}

function getCapitalFinancingEffect(yearsActive) {
    if (yearsActive <= 1) return 0.003;
    if (yearsActive <= 10) return 0.003 + (yearsActive / 10) * 0.007;
    if (yearsActive <= 20) return 0.010 + ((yearsActive - 10) / 10) * 0.010;
    if (yearsActive <= 30) return 0.020 + ((yearsActive - 20) / 10) * 0.010;
    return 0.030;
}

function getPreventiveCareEffect(yearsActive) {
    if (yearsActive <= 5) return 0;
    if (yearsActive <= 10) return 0.001 + ((yearsActive - 5) / 5) * 0.001;
    if (yearsActive <= 15) return 0.002 + ((yearsActive - 10) / 5) * 0.001;
    return 0.004;
}

function getAdminCascadeEffect(yearsActive) {
    if (yearsActive <= 5) return 0.009 * yearsActive;
    if (yearsActive <= 10) return 0.045 + ((yearsActive - 5) / 5) * 0.045;
    return 0.09;
}
